// Measuring tool, in two modes on one implementation.
//
// DISTANCE: click to drop points; a dashed line and a live readout track the
// running great-circle distance (with a rubber-band segment to the cursor).
// AREA: the same gesture closes into a ring, and the readout carries the
// spherical area with its perimeter beside it — the number a control map is
// actually asking for ("how much ground is that?").
//
// Finish (or double-click, on a mouse) freezes the result; the next click
// starts a fresh measurement. main.ts owns the two dock buttons and calls
// start(mode)/stop(). The maths lives in geo.ts, not here.

import * as maplibregl from 'maplibre-gl';

import { showDrawBar } from './drawbar';
import {
  fmtArea, fmtDistance, pathLengthM, polygonAreaM2, ringPerimeterM, type LngLat,
} from './geo';

const SRC = 'measure';
const FILL = 'measure-fill';
const LINE = 'measure-line';
const VERTS = 'measure-verts';

export type MeasureMode = 'distance' | 'area';

export interface Measure {
  /** Arm the tool. Re-arming in the same mode is a no-op; switching mode
   *  restarts, so the dock's two buttons can't leave half a ring behind. */
  start: (mode?: MeasureMode) => void;
  stop: () => void;
  isActive: () => boolean;
  mode: () => MeasureMode | null;
}

export function installMeasure(map: maplibregl.Map): Measure {
  let active = false;
  let mode: MeasureMode = 'distance';
  let finished = false;
  let pts: LngLat[] = [];
  let readout: HTMLDivElement | null = null;
  let cleanup: (() => void) | null = null;

  function ensureLayers(): void {
    if (!map.getSource(SRC)) {
      map.addSource(SRC, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
    }
    // The ring's fill goes under its own outline, so the dashes stay legible
    // over the tint.
    if (!map.getLayer(FILL)) {
      map.addLayer({
        id: FILL, type: 'fill', source: SRC,
        filter: ['==', ['geometry-type'], 'Polygon'],
        paint: { 'fill-color': '#f4b860', 'fill-opacity': 0.18 },
      }, map.getLayer(LINE) ? LINE : undefined);
    }
    if (!map.getLayer(LINE)) {
      map.addLayer({
        id: LINE, type: 'line', source: SRC,
        filter: ['==', ['geometry-type'], 'LineString'],
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': '#f4b860', 'line-width': 2.5, 'line-dasharray': [2, 1.4] },
      });
    }
    if (!map.getLayer(VERTS)) {
      map.addLayer({
        id: VERTS, type: 'circle', source: SRC,
        filter: ['==', ['geometry-type'], 'Point'],
        paint: {
          'circle-radius': 4, 'circle-color': '#ffffff',
          'circle-stroke-color': '#f4b860', 'circle-stroke-width': 2,
        },
      });
    }
  }

  /** The points as measured right now: the dropped ones, plus the cursor while
   *  the measurement is still running. */
  function working(extra?: LngLat): LngLat[] {
    return extra && !finished ? [...pts, extra] : pts;
  }

  /** What the measurement says, in words. Area leads with the area and keeps
   *  the perimeter beside it — both are the answer to "how big is that". */
  function value(extra?: LngLat): string {
    const line = working(extra);
    if (mode === 'area') {
      if (line.length < 3) return 'Click at least 3 corners';
      return `${fmtArea(polygonAreaM2(line))} · ${fmtDistance(ringPerimeterM(line))} around`;
    }
    return fmtDistance(pathLengthM(line));
  }

  const enough = (n: number): boolean => n >= (mode === 'area' ? 3 : 2);

  function draw(cursor?: LngLat): void {
    const feats: GeoJSON.Feature[] = [];
    const line = working(cursor);
    if (mode === 'area' && line.length >= 3) {
      feats.push({ type: 'Feature', properties: {},
        geometry: { type: 'Polygon', coordinates: [[...line, line[0]!]] } });
    }
    if (line.length >= 2) {
      // Area mode draws the closing edge too, so the shape reads as closed
      // while it is still being drawn.
      const path = mode === 'area' && line.length >= 3 ? [...line, line[0]!] : line;
      feats.push({ type: 'Feature', properties: {},
        geometry: { type: 'LineString', coordinates: path } });
    }
    for (const p of pts) {
      feats.push({ type: 'Feature', properties: {},
        geometry: { type: 'Point', coordinates: p } });
    }
    (map.getSource(SRC) as maplibregl.GeoJSONSource | undefined)
      ?.setData({ type: 'FeatureCollection', features: feats });
  }

  function say(text: string): void {
    if (!readout) {
      readout = document.createElement('div');
      readout.className = 'measure-readout';
      (map.getContainer().parentElement ?? map.getContainer()).appendChild(readout);
    }
    readout.textContent = text;
  }

  function start(next: MeasureMode = 'distance'): void {
    if (active && mode === next) return;
    if (active) stop();          // a mode switch is a fresh measurement
    active = true;
    mode = next;
    finished = false;
    pts = [];
    ensureLayers();
    draw();
    say(mode === 'area' ? 'Click the corners of an area' : 'Click to measure');
    map.getCanvas().style.cursor = 'crosshair';
    map.doubleClickZoom.disable();

    const done = () => {
      if (!enough(pts.length)) return;
      finished = true;
      draw();
      say(`${value()} · click to measure again`);
      bar.update(pts.length, value());
    };
    // Finish/Undo/Cancel, because a double TAP cannot end a line on a phone —
    // it lands two more points and a zoom instead (see drawbar.ts).
    const bar = showDrawBar({
      title: mode === 'area' ? 'Measure area' : 'Measure',
      minPoints: mode === 'area' ? 3 : 2,
      minHint: mode === 'area' ? 'Click at least 3 corners' : 'Click a second point',
      onFinish: done,
      onUndo: () => {
        pts.pop();
        finished = false;
        draw();
        say(enough(pts.length) ? value() : 'Click the next point…');
        bar.update(pts.length);
      },
      onCancel: () => stop(),
    });

    const onClick = (e: maplibregl.MapMouseEvent) => {
      if (finished) { pts = []; finished = false; }  // a new measurement
      pts.push([e.lngLat.lng, e.lngLat.lat]);
      draw();
      say(enough(pts.length) ? value() : 'Click the next point…');
      bar.update(pts.length, enough(pts.length) ? value() : undefined);
    };
    const onMove = (e: maplibregl.MapMouseEvent) => {
      if (!pts.length || finished) return;
      const c: LngLat = [e.lngLat.lng, e.lngLat.lat];
      draw(c);
      say(value(c));
    };
    const onDbl = (e: maplibregl.MapMouseEvent) => {
      e.preventDefault();
      done();
    };
    map.on('click', onClick);
    map.on('mousemove', onMove);
    map.on('dblclick', onDbl);
    cleanup = () => {
      bar.close();
      map.off('click', onClick);
      map.off('mousemove', onMove);
      map.off('dblclick', onDbl);
      map.doubleClickZoom.enable();
      map.getCanvas().style.cursor = '';
    };
  }

  function stop(): void {
    if (!active) return;
    active = false;
    cleanup?.();
    cleanup = null;
    pts = [];
    (map.getSource(SRC) as maplibregl.GeoJSONSource | undefined)
      ?.setData({ type: 'FeatureCollection', features: [] });
    readout?.remove();
    readout = null;
  }

  return { start, stop, isActive: () => active, mode: () => (active ? mode : null) };
}
